// v1.5.0 · Turismo MDD: plataforma de viajes tipo Uber para una flota de taxis (5 pantallas animadas)
import React from 'react';
import { useTick, Count, Head, Live, Kpi, Status, useReduced } from './kit';

const Car = ({ c = '#57D3FF' }) => (
  <g><rect x="-7" y="-4.5" width="14" height="9" rx="3" fill={c} stroke="#071925" strokeWidth="1.5" /><rect x="-3" y="-3" width="6" height="6" rx="1.5" fill="#071925" opacity=".5" /></g>
);
const MapBase = () => (
  <>
    <rect width="320" height="320" fill="#0B2232" />
    {['M0 80 C 100 70, 200 110, 320 90', 'M0 200 C 120 210, 220 170, 320 220', 'M90 0 C 100 120, 70 220, 110 320', 'M230 0 C 210 110, 250 220, 220 320'].map((d) => (
      <path key={d} d={d} fill="none" stroke="rgba(221,240,247,.1)" strokeWidth="9" strokeLinecap="round" />
    ))}
    <path d="M0 260 C 80 250, 160 290, 320 270" fill="none" stroke="rgba(46,155,214,.35)" strokeWidth="14" />
    <text x="250" y="300" fontSize="10" fill="#5E8BA6">Río Madre de Dios</text>
  </>
);
function Phone({ children, label }) {
  return (
    <div className="phone mx-auto" aria-label={label} role="img">
      <div className="phone-screen">{children}</div>
    </div>
  );
}

/* 1 · App del pasajero */
export function TurPasajero() {
  const t = useTick(1300);
  const ph = t % 7;
  return (
    <div className="grid h-full grid-cols-1 items-center gap-3 p-3 sm:grid-cols-[auto_1fr]">
      <Phone label="App del pasajero solicitando un viaje">
        <svg viewBox="0 0 320 320" className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice"><MapBase />
          <circle cx="150" cy="150" r="8" fill="#57D3FF" stroke="#071925" strokeWidth="3" />
          {ph >= 2 && ph < 4 && [0, 0.6, 1.2].map((d) => <circle key={d} cx="150" cy="150" r="20" fill="none" stroke="#57D3FF" strokeWidth="2" className="ring" style={{ '--d': `${d}s` }} />)}
          {ph >= 4 && <g className="move" style={{ transform: `translate(${210 - (ph - 4) * 20}px, ${90 + (ph - 4) * 18}px)` }}><Car /></g>}
        </svg>
        <div className="absolute inset-x-2 bottom-2 rounded-xl bg-[#0C2434]/95 p-2.5 text-[11px]">
          {ph < 2 && <div className="rise"><div className="font-bold">¿A dónde vas?</div><div className="mt-1.5 rounded-md bg-white/10 px-2 py-1.5">Aeropuerto Padre Aldamiz</div><div className="mt-2 rounded-md bg-[#2E8A24] py-1.5 text-center font-bold">Pedir taxi, S/ 12</div></div>}
          {ph >= 2 && ph < 4 && <div className="rise text-center"><div className="font-bold">Buscando conductor cercano…</div><div className="dim mt-1">3 unidades a menos de 2 km</div></div>}
          {ph >= 4 && <div className="rise"><div className="flex items-center justify-between"><span className="font-bold">Unidad 14 en camino</span><span className="num font-bold text-[#8BE07F]">{Math.max(1, 6 - (ph - 4) * 2)} min</span></div><div className="dim mt-1">Luis Q., Toyota Yaris, placa M3K-214</div><div className="mt-1.5 rounded-md bg-white/10 px-2 py-1 text-center">PIN del viaje <b className="num">48213</b></div></div>}
        </div>
      </Phone>
      <div className="flex flex-col gap-2">
        <Head title="App del pasajero" sub="Pide, sigue y verifica su taxi" right={<Live />} />
        <div className="grid grid-cols-2 gap-2">
          <Kpi label="Viajes hoy"><Count value={412 + t} /></Kpi>
          <Kpi label="Espera promedio" tone="ok"><Count value={3.8} dec={1} suffix=" min" /></Kpi>
        </div>
        <ul className="dim hidden space-y-1.5 text-[12px] sm:block">
          <li>● Tarifa fija por zona antes de confirmar</li>
          <li>● Conductor, vehículo y placa visibles</li>
          <li>● PIN de 5 dígitos para iniciar el viaje</li>
          <li>● Botón SOS y viaje compartido con la familia</li>
        </ul>
      </div>
    </div>
  );
}

/* 2 · Asignación automática por cercanía */
const DRIVERS = [[60, 60, 2.8], [250, 50, 2.1], [270, 240, 1.6], [70, 230, 0.9], [190, 140, 1.2], [140, 290, 3.1]];
export function TurMatching() {
  const t = useTick(2600);
  const ranked = DRIVERS.map((d, i) => ({ i, x: d[0], y: d[1], km: d[2] + ((t + i) % 3) * 0.1, busy: (t + i) % 5 === 0 }))
    .sort((a, b) => (a.busy - b.busy) || (a.km - b.km));
  const best = ranked[0];
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.4fr_1fr]">
      <div className="dash-panel relative min-h-0 overflow-hidden">
        <svg viewBox="0 0 320 320" className="h-full w-full" preserveAspectRatio="xMidYMid slice" role="img" aria-label="Pedido nuevo y conductores en un radio de búsqueda">
          <MapBase />
          <circle key={t} cx="150" cy="170" r="30" fill="rgba(87,211,255,.06)" stroke="rgba(87,211,255,.6)" className="ring" />
          <circle cx="150" cy="170" r="120" fill="none" stroke="rgba(87,211,255,.25)" strokeDasharray="4 5" />
          {ranked.slice(0, 3).filter((d) => !d.busy).map((d) => (
            <line key={`${t}-${d.i}`} x1="150" y1="170" x2={d.x} y2={d.y} stroke={d === best ? '#57D3FF' : 'rgba(221,240,247,.25)'} strokeWidth={d === best ? 2 : 1} pathLength="1" className="draw" />
          ))}
          {DRIVERS.map((d, i) => {
            const r = ranked.find((x) => x.i === i);
            return <g key={i} transform={`translate(${d[0]} ${d[1]})`} opacity={r.busy ? 0.35 : 1}><Car c={r === best ? '#57D3FF' : r.busy ? '#86A6B6' : '#DDF0F7'} /></g>;
          })}
          <g transform="translate(150 170)"><circle r="9" fill="#C7811F" stroke="#071925" strokeWidth="3" /></g>
          <text x="150" y="196" textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#F2C266">Plaza de Armas</text>
        </svg>
      </div>
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <Head title="Asignación automática" sub="En línea, GPS reciente y libre" right={<Live label="Pedido nuevo" />} />
        {ranked.slice(0, 4).map((d, k) => (
          <div key={d.i} className={`move flex items-center justify-between rounded-md px-2 py-1.5 text-[12px] ${k === 0 ? 'bg-[rgba(87,211,255,.12)]' : ''}`}>
            <span className="font-semibold">Unidad {d.i + 10}</span>
            <span className="num">{d.km.toFixed(1).replace('.', ',')} km</span>
            {d.busy ? <Status tone="warn">Ocupado</Status> : k === 0 ? <Status tone="ok">Asignado</Status> : <Status tone="info">Disponible</Status>}
          </div>
        ))}
        <p className="dim mt-auto text-[11.5px] leading-snug">Distancia real por coordenadas (Haversine), solo conductores con GPS de los últimos 5 minutos.</p>
      </div>
    </div>
  );
}

/* 3 · App del conductor */
export function TurConductor() {
  const t = useTick(1000);
  const left = 15 - (t % 16);
  const accepted = left <= 0;
  return (
    <div className="grid h-full grid-cols-1 items-center gap-3 p-3 sm:grid-cols-[auto_1fr]">
      <Phone label="App del conductor recibiendo un viaje">
        <div className="flex h-full flex-col gap-2 p-3 text-[11px]">
          <div className="flex items-center justify-between"><span className="font-bold">Unidad 14</span><Status tone="ok">En línea</Status></div>
          <div className="rounded-lg bg-white/5 p-2"><div className="dim">Ganado hoy</div><div className="text-[18px] font-extrabold"><Count value={186 + Math.floor(t / 4) * 12} prefix="S/ " /></div></div>
          {!accepted ? (
            <div key="req" className="rise mt-auto rounded-xl border border-[rgba(87,211,255,.5)] bg-[rgba(87,211,255,.08)] p-2.5">
              <div className="flex items-center justify-between"><b>Viaje nuevo</b>
                <svg viewBox="0 0 36 36" className="h-9 w-9" aria-label={`${left} segundos para aceptar`}>
                  <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#57D3FF" strokeWidth="3" strokeLinecap="round" pathLength="15" strokeDasharray="15" strokeDashoffset={15 - left}
                          style={{ transition: 'stroke-dashoffset 1s linear', transform: 'rotate(-90deg)', transformOrigin: 'center' }} />
                  <text x="18" y="22" textAnchor="middle" fontSize="11" fontWeight="800" fill="#DDF0F7">{left}</text>
                </svg>
              </div>
              <div className="dim mt-1">Plaza de Armas → Aeropuerto</div>
              <div className="mt-1 flex justify-between"><span>1,2 km de ti</span><b>S/ 12</b></div>
              <div className="mt-2 rounded-md bg-[#2E8A24] py-1.5 text-center font-bold">Aceptar</div>
            </div>
          ) : (
            <div key="ok" className="rise mt-auto rounded-xl bg-[rgba(79,191,106,.16)] p-2.5 text-center"><b className="text-[#8BE07F]">Viaje aceptado</b><div className="dim mt-1">Navegando al punto de recogida</div></div>
          )}
        </div>
      </Phone>
      <div className="flex flex-col gap-2">
        <Head title="App del conductor" sub="Recibe, acepta y cobra" />
        <div className="grid grid-cols-2 gap-2">
          <Kpi label="Conductores en línea" tone="ok"><Count value={38 + (t % 4)} /></Kpi>
          <Kpi label="Aceptación de viajes"><Count value={92} suffix=" %" /></Kpi>
        </div>
        <p className="dim hidden text-[12px] leading-snug sm:block">El viaje se ofrece primero al conductor más cercano; si no acepta en 15 segundos, pasa al siguiente.</p>
      </div>
    </div>
  );
}

/* 4 · Viaje en curso */
const TRIP = 'M40 280 C 70 220, 60 170, 120 150 S 200 120, 220 80 S 270 40, 290 30';
export function TurViaje() {
  const t = useTick(1000);
  const reduced = useReduced();
  const k = (t % 20) / 20;
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.5fr_1fr]">
      <div className="dash-panel relative min-h-0 overflow-hidden">
        <svg viewBox="0 0 320 320" className="h-full w-full" preserveAspectRatio="xMidYMid slice" role="img" aria-label="Recorrido del viaje en el mapa">
          <MapBase />
          <path d={TRIP} fill="none" stroke="rgba(87,211,255,.25)" strokeWidth="6" strokeLinecap="round" />
          <path d={TRIP} fill="none" stroke="#57D3FF" strokeWidth="3" strokeLinecap="round" pathLength="1" className="draw" />
          <circle cx="40" cy="280" r="6" fill="#C7811F" /><circle cx="290" cy="30" r="7" fill="none" stroke="#4FBF6A" strokeWidth="3" />
          <g>{!reduced && <animateMotion dur="20s" repeatCount="indefinite" path={TRIP} rotate="auto" />}<Car /></g>
        </svg>
        <div className="absolute left-3 top-3"><Status tone="ok">PIN verificado, viaje iniciado</Status></div>
      </div>
      <div className="flex min-h-0 flex-col gap-2">
        <Kpi label="Tarifa acordada">S/ 12,00</Kpi>
        <Kpi label="Tiempo restante"><Count value={Math.max(1, Math.round(9 * (1 - k)))} suffix=" min" dur={500} /></Kpi>
        <div className="dash-panel p-3">
          <div className="dim mb-2 text-[11px] font-semibold">Avance del recorrido</div>
          <div className="h-2 overflow-hidden rounded bg-white/10"><div className="bar-live-x h-full rounded bg-[#4FBF6A]" style={{ transform: `scaleX(${k})` }} /></div>
        </div>
        <div className="dash-panel hidden p-3 text-[11.5px] sm:block"><b>Compartido con:</b> <span className="dim">Ana Q. recibe la ubicación en vivo</span></div>
      </div>
    </div>
  );
}

/* 5 · Pedidos por WhatsApp con nota de voz */
export function TurWhatsapp() {
  const t = useTick(1500);
  const ph = t % 6;
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.2fr_1fr]">
      <div className="dash-panel flex min-h-0 flex-col gap-2 overflow-hidden bg-[#0A1F1A] p-3" aria-label="Conversación de WhatsApp">
        <div className="dim text-[11px] font-semibold">WhatsApp, Taxi Turismo MDD</div>
        <div className="rise ml-auto flex w-[78%] items-center gap-2 rounded-lg rounded-tr-sm bg-[#1F5B3A] px-2.5 py-2 text-[11.5px]">
          <span aria-hidden>▶</span>
          <span className="flex h-6 flex-1 items-center gap-[2px]">
            {Array.from({ length: 22 }, (_, i) => <span key={i} className="bar-live w-[3px] rounded-full bg-[#BFF0CF]" style={{ height: '100%', transform: `scaleY(${0.25 + Math.abs(Math.sin(i * 1.7 + t)) * 0.75})` }} />)}
          </span>
          <span className="num">0:06</span>
        </div>
        {ph >= 1 && <div className="rise w-[82%] rounded-lg rounded-tl-sm bg-[#16302A] px-2.5 py-2 text-[11.5px]"><span className="dim">Transcripción:</span> «Necesito un taxi en la Plaza de Armas, voy al aeropuerto»</div>}
        {ph >= 2 && <div className="rise ml-auto w-[60%] rounded-lg rounded-tr-sm bg-[#1F5B3A] px-2.5 py-2 text-[11.5px]">Ubicación compartida: Plaza de Armas</div>}
        {ph >= 3 && <div className="rise w-[86%] rounded-lg rounded-tl-sm bg-[#16302A] px-2.5 py-2 text-[11.5px]">Listo. La unidad 14 llega en 4 minutos. Tarifa S/ 12. Tu PIN es <b>48213</b>.</div>}
      </div>
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <Head title="Pedido creado automáticamente" sub="Voz, texto o ubicación" />
        {['Audio recibido', 'Voz convertida en texto', 'Origen y destino detectados', 'Tarifa por zona calculada', 'Conductor asignado'].map((s, i) => (
          <div key={s} className="flex items-center gap-2 text-[12px]">
            <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] transition-colors duration-300 ${i <= ph - 1 ? 'bg-[#4FBF6A] text-[#04121C]' : 'border border-white/20'}`}>{i <= ph - 1 ? '✓' : ''}</span>
            <span className={i <= ph - 1 ? '' : 'dim'}>{s}</span>
          </div>
        ))}
        <div className="mt-auto grid grid-cols-2 gap-2"><Kpi label="Pedidos por WhatsApp hoy"><Count value={137 + t} /></Kpi><Kpi label="Canales en un panel">3</Kpi></div>
      </div>
    </div>
  );
}

export const turismoScreens = [
  { t: 'Pasajero', C: TurPasajero }, { t: 'Asignación', C: TurMatching }, { t: 'Conductor', C: TurConductor },
  { t: 'Viaje', C: TurViaje }, { t: 'WhatsApp', C: TurWhatsapp },
];
